import { useState, useEffect, useRef } from 'react';
import { Map, Source, Layer, Popup, FullscreenControl, Marker } from 'react-map-gl';
import { useNavigate } from "react-router-dom";
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import axios from 'axios';
import { 
  Button, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Typography, 
  Box, 
  Divider, 
  CircularProgress 
} from '@mui/material';
import MapIcon from '@mui/icons-material/Map';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ElectoralCounter } from './ElectoralCounter';
import { MapControls } from './MapControls';
import { historicalResults } from './electionResults';
import Legend from './Legend';
import { STATE_FIPS_TO_NAME } from './stateMappings';
import { loadVariableSettings } from './services/variableService';

// Define the hook outside the component
const useCountAnimation = (endValue, duration = 1000, shouldAnimate, setShouldAnimate) => {
	const [count, setCount] = useState(endValue);
	
	useEffect(() => {
		if (!shouldAnimate) {
			setCount(endValue);
			return;
		}
		
		let startTimestamp = null;
		const startValue = count;
		
		const animate = (timestamp) => {
			if (!startTimestamp) startTimestamp = timestamp;
			const progress = timestamp - startTimestamp;
			
			const easeOutQuart = (x) => 1 - Math.pow(1 - x, 4);
			
			if (progress < duration) {
				const progressRatio = easeOutQuart(progress / duration);
				setCount(Math.round(startValue + (endValue - startValue) * progressRatio));
				requestAnimationFrame(animate);
			} else {
				setCount(endValue);
				setShouldAnimate(false);  // Reset animation flag when done
			}
		};
		
		requestAnimationFrame(animate);
	}, [endValue, duration, shouldAnimate, setShouldAnimate]);
	
	return count;
};

const MapPage = () => {
	const [data, setData] = useState([]);
	const [originalData, setOriginalData] = useState([]); // store original county data
	const [geoJsonData, setGeoJsonData] = useState([]);
	const [popupInfo, setPopupInfo] = useState(null);
	const [usePrediction, setUsePrediction] = useState(false);
	const [selectedModel, setSelectedModel] = useState('');
	const [models, setModels] = useState([]);
	const [year, setYear] = useState(2020);
	const mapRef = useRef(null);
	const navigate = useNavigate();
	const [accuracy, setAccuracy] = useState(null);
    const [fillLayerStyle, setFillLayerStyle] = useState({ id: 'county-fill',
    type: 'fill',
    paint: {
        'fill-color': [
            'match', // Use the match expression
            ['get', 'GEOID'], // Get the GEOID from the properties
            '1000', 'grey',
            'grey' // Default color for other counties
        ],
        'fill-opacity': 0.6,
    }})
	const [electoralCount, setElectoralCount] = useState({ democratic: 0, republican: 0 });
	const [dataLoaded, setDataLoaded] = useState(false);
	const [shouldAnimate, setShouldAnimate] = useState(false);
	const [isMapLoaded, setIsMapLoaded] = useState(false);
	const [showLoader, setShowLoader] = useState(true);  // New state for controlled loading
	const [mispredictedStates, setMispredictedStates] = useState([]);
  	const [loading, setLoading] = useState(true);
	const [isModelLoading, setIsModelLoading] = useState(false);
	const [variables, setVariables] = useState([]);

	// Load variables on component mount
	useEffect(() => {
		setVariables(loadVariableSettings());
	}, []);

	const stateFipsToName = {
		"01": "Alabama",
		"02": "Alaska",
		"04": "Arizona",
		"05": "Arkansas",
		"06": "California",
		"08": "Colorado",
		"09": "Connecticut",
		"10": "Delaware",
		"11": "District of Columbia",
		"12": "Florida",
		"13": "Georgia",
		"15": "Hawaii",
		"16": "Idaho",
		"17": "Illinois",
		"18": "Indiana",
		"19": "Iowa",
		"20": "Kansas",
		"21": "Kentucky",
		"22": "Louisiana",
		"23": "Maine",
		"24": "Maryland",
		"25": "Massachusetts",
		"26": "Michigan",
		"27": "Minnesota",
		"28": "Mississippi",
		"29": "Missouri",
		"30": "Montana",
		"31": "Nebraska",
		"32": "Nevada",
		"33": "New Hampshire",
		"34": "New Jersey",
		"35": "New Mexico",
		"36": "New York",
		"37": "North Carolina",
		"38": "North Dakota",
		"39": "Ohio",
		"40": "Oklahoma",
		"41": "Oregon",
		"42": "Pennsylvania",
		"44": "Rhode Island",
		"45": "South Carolina",
		"46": "South Dakota",
		"47": "Tennessee",
		"48": "Texas",
		"49": "Utah",
		"50": "Vermont",
		"51": "Virginia",
		"53": "Washington",
		"54": "West Virginia",
		"55": "Wisconsin",
		"56": "Wyoming",
		"61": "Maine-New Hampshire-Vermont",
		"62": "Massachusetts-Rhode Island",
		"63": "Minnesota-Iowa-Missouri-Kansas-Nebraska-S.Dakota-N.Dakota",
		"64": "Maryland-Delaware",
		"65": "Montana-Idaho-Wyoming",
		"66": "Utah-Nevada",
		"67": "Arizona-New Mexico",
		"68": "Alaska-Hawaii",
		"72": "Puerto Rico",
		"97": "Military/Mil. Reservation",
		"99": "State not identified",
	  };
	  

	useEffect(() => {
		axios.get(`${process.env.REACT_APP_API_URL}/db/geojson`)
		  .then((response) => {
			setGeoJsonData(response.data[0]);
		  });
	  }, []);



	  useEffect(() => {
		axios.get(`${process.env.REACT_APP_API_URL}/db/counties/${year}`)
		  .then(response => setOriginalData(response.data)); // fetch and store original data
	  }, [year]);

	
	  useEffect(() => {
		if (usePrediction && selectedModel) {
		  setIsModelLoading(true); // Start loading
		  const startTime = Date.now(); // Record start time
		  
		  axios.get(`${process.env.REACT_APP_API_URL}/db/prediction/${selectedModel}`)
			.then(response => {
			  console.log("Fetched prediction data:", response.data);
			  setData(response.data);
			})
			.catch(error => {
			  console.error("Error fetching prediction data:", error);
			})
			.finally(() => {
			  // Calculate how long the request took
			  const elapsedTime = Date.now() - startTime;
			  const remainingTime = Math.max(5000 - elapsedTime, 0);
			  
			  // Wait for remaining time to ensure minimum 5 second display
			  setTimeout(() => {
				setIsModelLoading(false);
			  }, remainingTime);
			});
		} else {
		  setData(originalData);
		}
	  }, [originalData, selectedModel, usePrediction]);

	  useEffect(() => {
		console.log("Data changed:", data);
		if (data && data.length > 0) {
		  console.log("Updating colors with data:", data);
		  updateMapColors(data);
		}
	  }, [data]);
	
	  
	  const updateMapColors = (dataToColor) => {
		if (!dataToColor) return;

		const newColors = calculateElectoralVotes(dataToColor);
		setElectoralCount(newColors);
	  
		let paintLayerArray = ['match', ['get', 'GEOID']];
		dataToColor.forEach(county => {
		  if (county && county.state_county) { // state_county is fips for actual data
			let color;
			if (county.winner === 0) {
			  color = 'blue'; // Fully blue
			} else if (county.winner === 1) {
			  color = 'red'; // Fully red
			} else {
			  // interpolate colors based on the value of 'winner'
			  if (county.winner <= 0.5) {
				color = 'blue';
			  } else {
				const intensity = Math.round(255 * (county.winner - 0.5) * 2);
				color = `rgb(${intensity}, 0, 0)`; // Darker red as it approaches 1
			  }
			}
			paintLayerArray.push(county.state_county.toString(), color);
		  }
		  else if (county && county.county_fips) { // county_fips is fips for prediction data
			let color;
			if (county.prediction === 0) {
			  color = 'blue'; // Fully blue
			} else if (county.prediction === 1) {
			  color = 'red'; // Fully red
			} else {
			  // Interpolate colors based on the value of 'prediction'
			  if (county.prediction <= 0.5) {
				const intensity = Math.round(255 * (0.5 - county.prediction) * 10);
				color = `rgb(0, 0, ${intensity})`;
			  } else {
				const intensity = Math.round(255 * (county.prediction - 0.5) * 10);
				color = `rgb(${intensity}, 0, 0)`;
			  }
			}
			paintLayerArray.push(county.county_fips.toString(), color);
		  }
		  else {
			console.log("Missing 'state_county' or 'winner' in county data", county);
		  }
		});
		paintLayerArray.push('grey'); // Default color for unmatched GEOID

		setFillLayerStyle({
		  ...fillLayerStyle,
		  paint: { 'fill-color': paintLayerArray, 'fill-opacity': 0.4 }
		});
	  };
	  
	  
	  useEffect(() => {
		if (usePrediction) {
		  axios.get(`${process.env.REACT_APP_API_URL}/db/prediction/models`)
			.then(res => {
			  console.log("Fetched models:", res.data); // Debug log
			  setModels(res.data);
			  if (res.data.length > 0 && !selectedModel) {
				setSelectedModel(res.data[0]);  // automatically select the first model
			  }
			})
			.catch(error => {
			  console.error("Error fetching models:", error);
			});
		} else {
		  setSelectedModel('');  // clear model selection when not in prediction mode
		}
	  }, [usePrediction]); // Only re-run when usePrediction changes
	  

	  useEffect(() => {
		const fetchYear = usePrediction && selectedModel ? extractYearFromModel(selectedModel) : year;
		console.log(fetchYear);
		axios.get(`${process.env.REACT_APP_API_URL}/db/counties/${fetchYear}`)
			.then(response => {
				setOriginalData(response.data);
			})
			.catch(error => {
				console.error("Error fetching data:", error);
			});
	}, [year, selectedModel, usePrediction]);
	
	
	  const handleYearChange = (e) => {
		setShouldAnimate(true);
		setYear(e.target.value);
	  };
	
	  const handleModelChange = (e) => {
		setShouldAnimate(true);
		setSelectedModel(e.target.value);
	  };
	
	  const handleTogglePrediction = () => {
		setUsePrediction(!usePrediction);
	  };

	  const extractYearFromModel = (modelName) => {
		return modelName.split('_')[1];
	  };
	
	  
	
	const layerStyle = {
	id: 'county-boundaries',
	type: 'line',
	paint: {
		'line-width': 0.8,
		'line-color': '#a0a0a0',
	},
	};

		
	const handleMouseMove = (e) => {
		if (!mapRef.current) return;
		const map = mapRef.current.getMap();
		const features = map.queryRenderedFeatures(e.point, { layers: ['county-fill'] });
	
		if (features.length > 0) {
		  const feature = features[0];
		  const countyFIPS = feature.properties.GEOID;
		  const detailsForYear = originalData.find(county => county.state_county && county.state_county.toString() === countyFIPS);
		  
		  if (detailsForYear) {
			setPopupInfo({
			  details: detailsForYear,
			  longitude: e.lngLat.lng,
			  latitude: e.lngLat.lat,
			  stateName: feature.properties.STATEFP,
			});
		  } else {
			setPopupInfo(null);
		  }
		} else {
		  setPopupInfo(null);
		}
	  };
    
    const handleMouseClick = (e) =>
    {
        if (!mapRef.current) return;
		const map = mapRef.current.getMap();
		const features = map.queryRenderedFeatures(e.point, { layers: ['county-fill'] });
	
		if (features.length > 0) {
		  const feature = features[0];
		  const countyFIPS = feature.properties.GEOID;
		  const detailsForYear = originalData.find(county => county.state_county && county.state_county.toString() === countyFIPS);
		  
		  if (detailsForYear) {
			console.log("Clicked on: ", detailsForYear);
            navigate(`/trends/${countyFIPS}/${stateFipsToName[feature.properties.STATEFP]}`);
          }
		}
    }
	
	const calculateElectoralVotes = (data) => {
		const electoralVotes = {
			'AL': 9, 'AK': 3, 'AZ': 11, 'AR': 6, 'CA': 54, 'CO': 10, 'CT': 7,
			'DE': 3, 'FL': 30, 'GA': 16, 'HI': 4, 'ID': 4, 'IL': 19, 'IN': 11,
			'IA': 6, 'KS': 6, 'KY': 8, 'LA': 8, 'ME': 4, 'MD': 10, 'MA': 11,
			'MI': 15, 'MN': 10, 'MS': 6, 'MO': 10, 'MT': 4, 'NE': 5, 'NV': 6,
			'NH': 4, 'NJ': 14, 'NM': 5, 'NY': 28, 'NC': 16, 'ND': 3, 'OH': 17,
			'OK': 7, 'OR': 8, 'PA': 19, 'RI': 4, 'SC': 9, 'SD': 3, 'TN': 11,
			'TX': 40, 'UT': 6, 'VT': 3, 'VA': 13, 'WA': 12, 'WV': 4, 'WI': 10,
			'WY': 3, 'DC': 3
		  };

	  let demVotes = 0;
	  let repVotes = 0;
	  const stateResults = {};

	  data.forEach(county => {
		const fips = (county.state_county || county.county_fips).toString().padStart(5, '0');
		const stateFips = fips.slice(0, 2);
		const stateAbbr = STATE_FIPS_TO_NAME[stateFips];

		if (!stateAbbr) return;

		if (!stateResults[stateAbbr]) {
		  stateResults[stateAbbr] = { demVotes: 0, repVotes: 0 };
		}

		// For prediction data
		if (county.prediction !== undefined) {
		  if (county.prediction < 0.5) {
			stateResults[stateAbbr].demVotes++;
		  } else {
			stateResults[stateAbbr].repVotes++;
		  }
		}
		// For actual election data
		else if (county.winner !== undefined) {
		  if (county.winner === 0) {
			stateResults[stateAbbr].demVotes++;
		  } else {
			stateResults[stateAbbr].repVotes++;
		  }
		}
	  });

	  // Calculate electoral votes based on state totals
	  Object.entries(stateResults).forEach(([stateAbbr, counts]) => {
		if (counts.demVotes > counts.repVotes) {
		  demVotes += electoralVotes[stateAbbr] || 0;
		} else if (counts.repVotes > counts.demVotes) {
		  repVotes += electoralVotes[stateAbbr] || 0;
		}
	  });

	  return { democratic: demVotes, republican: repVotes };
	};

	useEffect(() => {
		if (!usePrediction) {
		  const votes = historicalResults[year];
		  const totalVotes = votes.democratic + votes.republican;
		  if (totalVotes !== 538) {
			console.warn(`Electoral vote mismatch for year ${year}: got ${totalVotes}, expected 538`);
		  }
		  setElectoralCount(votes);
		  setDataLoaded(true);
		  setMispredictedStates([]);
		} else if (selectedModel) {
		  // Fetch prediction model data and actual county data
		  const fetchPredictionData = axios.get(
			`${process.env.REACT_APP_API_URL}/db/prediction/${selectedModel}`
		  );
		  const fetchCountyData = axios.get(
			`${process.env.REACT_APP_API_URL}/db/counties/${year}`
		  );
	  
		  Promise.all([fetchPredictionData, fetchCountyData])
			.then(([predictionResponse, countyResponse]) => {
			  const predictionData = predictionResponse.data;
			  const countyData = countyResponse.data;
	  
			  // Cross-check rows and calculate accuracy and mispredictions
			  let accurateCount = 0;
			  let totalCount = 0;
			  const mispredictedStates = [];
	  
			  predictionData.forEach((predictionRow) => {
				const matchingCountyRow = countyData.find(
				  (countyRow) => countyRow.state_county === predictionRow.state_county
				);
	  
				if (matchingCountyRow) {
				  totalCount++;
				  if (Number(predictionRow.prediction) === Number(matchingCountyRow.winner)) {
					accurateCount++;
				  } else {
					const stateCounty = String(matchingCountyRow.state_county).padStart(5, "0"); // Ensure it's 5 digits
					const stateCode = stateCounty.slice(0, 2); // First two digits for state
					const stateName = stateFipsToName[stateCode] || "Unknown State";
	  
					// Get electoral votes for the state
					const electoralVotes = {
					  'Alabama': 9, 'Alaska': 3, 'Arizona': 11, 'Arkansas': 6, 'California': 54, 'Colorado': 10, 'Connecticut': 7,
					  'Delaware': 3, 'Florida': 30, 'Georgia': 16, 'Hawaii': 4, 'Idaho': 4, 'Illinois': 19, 'Indiana': 11,
					  'Iowa': 6, 'Kansas': 6, 'Kentucky': 8, 'Louisiana': 8, 'Maine': 4, 'Maryland': 10, 'Massachusetts': 11,
					  'Michigan': 15, 'Minnesota': 10, 'Mississippi': 6, 'Missouri': 10, 'Montana': 4, 'Nebraska': 5, 'Nevada': 6,
					  'New Hampshire': 4, 'New Jersey': 14, 'New Mexico': 5, 'New York': 28, 'North Carolina': 16, 'North Dakota': 3,
					  'Ohio': 17, 'Oklahoma': 7, 'Oregon': 8, 'Pennsylvania': 19, 'Rhode Island': 4, 'South Carolina': 9,
					  'South Dakota': 3, 'Tennessee': 11, 'Texas': 40, 'Utah': 6, 'Vermont': 3, 'Virginia': 13, 'Washington': 12,
					  'West Virginia': 4, 'Wisconsin': 10, 'Wyoming': 3, 'District of Columbia': 3
					};
	  
					mispredictedStates.push({
					  state: stateName,
					  predictedWinner: predictionRow.prediction,
					  actualWinner: matchingCountyRow.winner,
					  electoralVotes: electoralVotes[stateName] || 0
					});
				  }
				}
			  });
	  
			  const accuracy = totalCount
				? ((accurateCount / totalCount) * 100).toFixed(2)
				: "N/A";
	  
			  // Calculate electoral votes including mispredictions
			  const votes = calculateElectoralVotes(predictionData);
			  
			  // Add DC's electoral votes
			  votes.democratic += 3;
			  
			  setElectoralCount(votes);
			  setAccuracy(accuracy); // Update accuracy state
			  setMispredictedStates(mispredictedStates); // Store mispredicted states
			  setDataLoaded(true);
	  
			  console.log("Total Count:", totalCount);
			  console.log("Accurate Count:", accurateCount);
			  console.log("Mispredicted States:", mispredictedStates);
			})
			.catch((error) => {
			  console.error("Error fetching data:", error);
			  setDataLoaded(true);
			});
		}
	  }, [year, usePrediction, selectedModel, calculateElectoralVotes, historicalResults]);			
	
	// Add this handler for when the map finishes loading
	const onMapLoad = () => {
		setIsMapLoaded(true);
		setTimeout(() => {
			setShowLoader(false);
		}, 2000);  // 2000ms = 2 seconds
	};

	// Filter mispredicted states into democratic and republican categories
	const democratMispredictions = mispredictedStates.filter(state => state.predictedWinner === 0);
	const republicanMispredictions = mispredictedStates.filter(state => state.predictedWinner === 1);

	// Add these right before the return statement
	const uniqueDemocratMispredictions = [];
	const uniqueRepublicanMispredictions = [];

	const democratSet = new Set();
	const republicanSet = new Set();

	mispredictedStates.forEach(state => {
	  if (state.predictedWinner === 0 && !democratSet.has(state.state)) {
		democratSet.add(state.state);
		uniqueDemocratMispredictions.push(state);
	  } else if (state.predictedWinner === 1 && !republicanSet.has(state.state)) {
		republicanSet.add(state.state);
		uniqueRepublicanMispredictions.push(state);
	  }
	});

	return (
		<div style={{ width: "100vw", height: "93vh", position: "relative" }}>
		  {(showLoader || !isMapLoaded || !dataLoaded) && (
			<Box
			  sx={{
				position: 'absolute',
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				backgroundColor: 'rgba(255, 255, 255, 0.9)',
				zIndex: 1000,
			  }}
			>
			  <CircularProgress size={60} />
			  <Typography variant="h6" sx={{ mt: 2 }}>
				Loading Map... Please Allow Time for Map to Load
			  </Typography>
			</Box>
		  )}
		  {isModelLoading && (
			<Box
			  sx={{
				position: 'absolute',
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				backgroundColor: 'rgba(255, 255, 255, 0.9)',
				zIndex: 1000,
			  }}
			>
			  <CircularProgress size={60} />
			  <Typography variant="h6" sx={{ mt: 2 }}>
				Loading Prediction Model...
			  </Typography>
			</Box>
		  )}
		  {/* Accuracy Display */}
		  {usePrediction && selectedModel && accuracy !== null && (
			<Box
			  sx={{
				position: "absolute",
				top: "1rem",
				right: "1rem",
				backgroundColor: "rgba(255, 255, 255, 0.9)",
				padding: "0.75rem",
				borderRadius: "8px",
				boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
				zIndex: 1100,
			  }}
			>
			  <Typography variant="h6" sx={{ 
				textAlign: "center",
				fontSize: '1rem',
				marginBottom: 1
			  }}>
				Prediction Model Accuracy
			  </Typography>
			  <Typography
				variant="h4"
				sx={{ 
				  textAlign: "center", 
				  fontWeight: "bold", 
				  marginBottom: 1,
				  fontSize: '2rem'
				}}
			  >
				{accuracy}%
			  </Typography>
			</Box>
		  )}
		  <>
			{/* Electoral Counter */}
			<Box sx={{
				position: 'absolute',
				bottom: 320,  // Position it above MapControls
				left: 10,
				zIndex: 1000,
				width: '350px',
			}}>
				<Box sx={{
					backgroundColor: 'white',
					borderRadius: '8px',
					boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
					width: '100%',
					overflow: 'hidden',
					height: '80px'
				}}>
					<ElectoralCounter
						democraticCount={electoralCount.democratic}
						republicanCount={electoralCount.republican}
						shouldAnimate={shouldAnimate}
						setShouldAnimate={setShouldAnimate}
					/>
				</Box>
			</Box>

			{/* MapControls - Original position at bottom */}
			<Box sx={{
				position: 'absolute',
				bottom: 60,
				left: 10,
				zIndex: 1000,
				width: '220px'  // Original MapControls width
			}}>
				<MapControls
					usePrediction={usePrediction}
					handleTogglePrediction={handleTogglePrediction}
					selectedModel={selectedModel}
					handleModelChange={handleModelChange}
					models={models}
					year={year}
					handleYearChange={handleYearChange}
				/>
			</Box>
			{/* Mispredicted States */ }
			{usePrediction && selectedModel && mispredictedStates.length > 0 && (
				<Box
					sx={{
						position: "absolute", 
						bottom: "10rem",
						right: "1rem",
						backgroundColor: "rgba(255, 255, 255, 0.9)",
						padding: "0.75rem",
						borderRadius: "8px",
						boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
						zIndex: 1100,
						maxHeight: "350px",
						overflowY: "auto",
						width: "240px", // Increased width to accommodate text better
					}}
				>
					<Typography variant="h6" sx={{ 
						textAlign: "center",
						marginBottom: 1,
						fontSize: '0.9rem'
					}}>
						Mispredicted States
					</Typography>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
						<Box>
							<Typography variant="body1" sx={{ 
								color: '#0000CC',
								fontWeight: 'bold',
								textAlign: 'center', 
								fontSize: '0.8rem',
								marginBottom: '0.5rem'
							}}>
								Predicted Democrat
								<br />
								<span style={{ fontSize: '0.7rem', fontWeight: 'normal' }}>(Actually Republican)</span>
							</Typography>
							<ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
								{uniqueDemocratMispredictions.map((state, index) => (
									<li key={state.state} style={{ marginBottom: '4px', textAlign: 'center' }}>
										<Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
											{state.state} ({state.electoralVotes})
										</Typography>
									</li>
								))}
							</ul>
						</Box>
						<Divider />
						<Box>
							<Typography variant="body1" sx={{ 
								color: '#CC0000',
								fontWeight: 'bold',
								textAlign: 'center',
								fontSize: '0.8rem',
								marginBottom: '0.5rem'
							}}>
								Predicted Republican
								<br />
								<span style={{ fontSize: '0.7rem', fontWeight: 'normal' }}>(Actually Democrat)</span>
							</Typography>
							<ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
								{uniqueRepublicanMispredictions.map((state, index) => (
									<li key={state.state} style={{ marginBottom: '4px', textAlign: 'center' }}>
										<Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
											{state.state} ({state.electoralVotes})
										</Typography>
									</li>
								))}
							</ul>
						</Box>
					</Box>
					<Typography 
						variant="body2" 
						sx={{ 
							marginTop: 1,
							textAlign: "center",
							borderTop: '1px solid #eee',
							paddingTop: 0.75,
							color: '#666',
							fontSize: '0.7rem'
						}}
					>
						Note: Electoral votes misplaced due to misprediction
					</Typography>
				</Box>
			)}
		  </>
		  <Map
			ref={mapRef}
			initialViewState={{
			  longitude: -98,
			  latitude: 38,
			  zoom: 3.7,
			}}
			minZoom={3}
			maxZoom={12}
			dragRotate={false}
			touchRotate={false}
			maxPitch={0}
			minPitch={0}
			maxBounds={[
			  [-190.276413, 15.465334], // Southwest coordinates [lng, lat] - Extended further west for Hawaii
			  [-52.23304, 71.5388], // Northeast coordinates [lng, lat]  
			]}
			style={{ width: "100%", height: "100%" }}
			mapStyle="mapbox://styles/mapbox/light-v10?optimize=true"
			mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
			onMouseMove={handleMouseMove}
			onClick={handleMouseClick}
			onLoad={onMapLoad}
		  >
			{geoJsonData && (
			  <Source type="geojson" data={geoJsonData}>
				<Layer {...layerStyle} />
				<Layer {...fillLayerStyle} />
			  </Source>
			)}
			{popupInfo && (
			  <Popup
				longitude={popupInfo.longitude}
				latitude={popupInfo.latitude}
				closeButton={false}
				closeOnClick={false}
				anchor="top"
			  >
				<p>
					<strong>Year: </strong>
					{popupInfo.details.year}
				</p>
				<p>
					<strong>State: </strong>
					{stateFipsToName[popupInfo.stateName] || "Unknown"}
				</p>
				{variables
					.filter(v => v.showInPopup)
					.map(variable => {
						const value = popupInfo.details[variable.id];
						if (value === undefined) return null;
						
						let displayValue = value;
						if (variable.id.includes('income')) {
							displayValue = `$${(value / 10).toLocaleString()}`;
						} else if (typeof value === 'number') {
							displayValue = `${value}`;
						}
						else {
							displayValue = `${value}%`;
						}
						
						return (
							<p key={variable.id}>
								<strong>{variable.label}: </strong>
								{displayValue}
							</p>
						);
					})}
			  </Popup>
			)}
		  </Map>
		  <Legend />
		</div>
	  );	  
};

export default MapPage;